/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';

const styles = {
  searchTerm: css`
    padding: 2px;
    color: inherit;
    white-space: unset;
    border-radius: var(--cds-border-radius-50);
    scroll-margin-top: var(--cds-spacing-200);
    background-color: var(--cds-color-emphasis-tertiary-background-weak);

    &.active {
      animation: focus 0.1s ease-in-out forwards;
    }

    @keyframes focus {
      0% {
        background-color: transparent;
      }

      100% {
        background-color: var(--cds-color-emphasis-tertiary-background-default);
      }
    }
  `,
};

/**
 * Individual search highlight component that renders a <mark> element.
 * This is the visual representation of each search match.
 * @param children - The search term text to highlight
 */
export const SearchTermHighlightRenderer = ({ children }: { children: React.ReactNode }) => {
  return (
    <mark className="coach-search-term-highlight" css={styles.searchTerm} tabIndex={-1}>
      {children}
    </mark>
  );
};

/**
 * Custom hook for managing search term reporting and active state management.
 * This hook handles:
 * 1. Reporting search term highlights to the context after rendering
 * 2. Managing the "active" class for the currently selected search result
 *
 * @param containerRef - Reference to the container element that contains search highlights
 * @param searchTerm - The current search term
 * @returns Object with any needed utilities (currently empty, but extensible)
 */
export const useSearchTermHighlights = (containerRef: React.RefObject<HTMLElement>, searchTerm: string) => {
  const { reportSearchTerm, activeResultIndex, results } = useCoachChatContext();

  // Report all search terms after rendering is complete
  React.useEffect(() => {
    if (!searchTerm || !containerRef.current) {
      return () => {};
    }

    // Add a small delay to ensure all rendering (including animations) is complete
    const timeoutId = setTimeout(() => {
      if (containerRef.current) {
        const searchHighlights = containerRef.current.querySelectorAll('.coach-search-term-highlight');

        // Convert NodeList to array of refs and report all at once
        const highlightRefs = Array.from(searchHighlights).map((element) => {
          const ref = { current: element as HTMLElement };
          return ref;
        });

        highlightRefs.forEach((ref, index) => {
          reportSearchTerm(ref);
        });
      }
    }, 100); // 100ms delay to ensure rendering is complete

    return () => clearTimeout(timeoutId);
  }, [searchTerm, reportSearchTerm, containerRef]);

  // Handle active class management when activeResultIndex changes
  React.useEffect(() => {
    if (!containerRef.current || activeResultIndex === null) {
      return;
    }

    // Remove active class from all highlights in this container
    const searchHighlights = containerRef.current.querySelectorAll('.coach-search-term-highlight');
    searchHighlights.forEach((highlight) => {
      highlight.classList.remove('active');
    });

    // Find the active result by comparing with all current highlights in the DOM
    // We need to do this because stored refs may be stale if code blocks were re-rendered
    const allCurrentHighlights = document.querySelectorAll('.coach-search-term-highlight');
    const activeHighlight = allCurrentHighlights[activeResultIndex];

    // Check if the active highlight is within this container
    if (activeHighlight && containerRef.current.contains(activeHighlight)) {
      activeHighlight.classList.add('active');
    }
  }, [activeResultIndex, results, containerRef]);

  // Return any utilities that consumers might need (extensible for future needs)
  return {};
};
